import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { generateUniqueKey, getErrorMessage, isFresh } from "../../utils/helpers";
import { fetchCampaigns as fetchCampaignsAPI, fetchCampaignsNew, fetchInvitedCampaigns } from "../../services/api/campaign";
import { Campaign, Campaigns } from "../../types/sponsorships/campaigns";
import { apiConfigs } from "../../constants/apiConfigs";

export interface CampaignFilters {
    page: number,
    [key: string]: any
}

export const defaultCampaignFilters: CampaignFilters = {
    page: 1,
}

export interface CampaignsSlice extends Campaigns {
    filters: CampaignFilters,
    applied_filter_id: string | null,
    total: number,
    filtered_lists: Record<string, { // filterId
        list: Campaign[],
        count: number,
        page: number,
        last_fetched_at: number | null,
        expired: boolean,
        has_more: boolean
    }>,
    error: string | null,
    page: number,
    loading: boolean,
    last_fetched_at: number | null,
    has_more: boolean,
    invited: {
        list: Campaign[],
        loading: boolean,
        error: string | null,
        last_fetched_at: number | null
    }
}

export const initialState: CampaignsSlice = {
    filters: defaultCampaignFilters,
    applied_filter_id: null,
    total: 0,
    filtered_lists: {},
    count: 0,
    items: [],
    error: null,
    loading: false,
    last_fetched_at: null,
    page: 1,
    has_more: true,
    invited: {
        list: [],
        loading: true,
        error: null,
        last_fetched_at: null
    },
}


const STALE_TIME = apiConfigs.FETCH_CAMPAIGNS.staleTime;

export const fetchCampaigns = createAsyncThunk(
    'campaigns/fetchCampaigns',
    async (page: number, { getState, rejectWithValue }) => {
        const state = getState() as { campaigns: CampaignsSlice };
        if(page === 1 && isFresh(state.campaigns.last_fetched_at, STALE_TIME)){
            return rejectWithValue("CACHED");
        }
        try {
            const campaigns = await fetchCampaignsAPI(page);
            return {campaigns, page};
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch campaigns'));
        }
    }
);

export const fetchCampaignsThunk = createAsyncThunk<{items: Campaign[], count: number, filters: CampaignFilters, page: number, filterId: string}, { filters: CampaignFilters, force?: boolean }, { rejectValue: string }>(
    'campaigns/fetchCampaignsThunk',
    async ({ filters, force }, { getState, rejectWithValue }) => {
        const state = getState() as { campaigns: CampaignsSlice };
        const filterId = generateUniqueKey(filters, ['page']);
        const STALE_TIME = apiConfigs.FETCH_CAMPAIGNS.staleTime;
        if (filters.page === 1 && !force && isFresh(state.campaigns.filtered_lists?.[filterId]?.last_fetched_at, STALE_TIME)) {
            return rejectWithValue("CACHED");
        }

        const { count, items } = await fetchCampaignsNew(filters);

        return { count, items, filters, page: filters.page, filterId }
    }
);

const INVITED_CAMPAIGNS_STALE_TIME = apiConfigs.FETCH_INVITED_CAMPAIGNS.staleTime;

export const fetchInvitedCampaignsThunk = createAsyncThunk(
    'campaigns/fetchInvitedCampaigns',
    async (_: void, { getState, rejectWithValue }) => {
        const state = getState() as { campaigns: CampaignsSlice };
        if(isFresh(state.campaigns.invited.last_fetched_at, INVITED_CAMPAIGNS_STALE_TIME)){
            const campaigns = state.campaigns.invited.list;
            return {campaigns, cached: true}
        }
        try {
            const campaigns = await fetchInvitedCampaigns();
            return {campaigns};
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch campaigns'));
        }
    }
);

const campaignsSlice = createSlice({
    name: 'campaigns',
    initialState,
    reducers: {
        updateCampaignFilters: (state, action) => {
            state.filters = action.payload;
        },
        resetCampaigns: (state) => {
            state.items = [];
            state.count = 0;
            state.page = 1;
            state.loading = false;
            state.error = null;
            state.last_fetched_at = null;
            state.has_more = true;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCampaigns.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCampaigns.fulfilled, (state, action) => {
                state.items = action.payload.page === 1 ? 
                                action.payload.campaigns.items : 
                                [...state.items, ...action.payload.campaigns.items];

                state.count = action.payload.campaigns.count;
                state.page = action.payload.page;
                state.loading = false;
                state.has_more = action.payload.campaigns.count > state.items.length;
                state.last_fetched_at = Date.now();
            })
            .addCase(fetchCampaigns.rejected, (state, action) => {
                state.loading = false;
            })
            .addCase(fetchInvitedCampaignsThunk.pending, (state) => {
                state.invited.loading = true;
                state.invited.error = null;
            })
            .addCase(fetchInvitedCampaignsThunk.fulfilled, (state, {payload}) => {
                const {campaigns, cached} = payload;
                state.invited.list = campaigns;
                state.invited.loading = false;
                state.invited.error = null;
                if(!cached){
                    state.invited.last_fetched_at = Date.now();
                }
            })
            .addCase(fetchInvitedCampaignsThunk.rejected, (state) => {
                state.invited.loading = false;
            })
            .addCase(fetchCampaignsThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCampaignsThunk.fulfilled, (state, { payload }) => {
                const { items, count, filters, page, filterId } = payload;
                const existingList = state.filtered_lists?.[filterId]?.list ?? [];
                const list = filters.page === 1 ? items : [...existingList, ...items];
                
                state.filtered_lists = {
                    ...state.filtered_lists,
                    [filterId]: {
                        list,
                        page: filters.page,
                        expired: false,
                        last_fetched_at: Date.now(),
                        has_more: count > list.length,
                        count
                    }
                };
                state.applied_filter_id = filterId;
                state.loading = false;
                state.error = null;
            })
            .addCase(fetchCampaignsThunk.rejected, (state, action) => {
                state.loading = false;
                if (action.payload !== "CACHED") {
                    state.error = action.payload || 'Failed to fetch campaigns';
                }
            })
    }
})

export const { updateCampaignFilters } = campaignsSlice.actions;

export default campaignsSlice.reducer;